import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { Bold, Image as ImageIcon, Italic, Link as LinkIcon, List, ListOrdered } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUploadAnnouncementInlineImage } from '@/hooks/use-announcements'
import { getApiErrorMessage } from '@/lib/api'
import { publicDocumentUrl } from '@/lib/documents'

const TOOLBAR_BUTTON_CLS =
  'rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-ink aria-pressed:bg-muted aria-pressed:text-ink disabled:pointer-events-none disabled:opacity-40'

export interface RichTextEditorHandle {
  /** Images inserted before the announcement had an id — keyed by the local blob: URL used as
   * the live preview, so the caller can upload each one once a real id exists and swap the blob
   * URL for the real hosted one in the saved body. */
  getPendingImages(): Map<string, File>
}

export const RichTextEditor = forwardRef<
  RichTextEditorHandle,
  {
    value: string
    onChange: (html: string) => void
    id?: string
    /** Once set, newly-inserted images upload immediately. Omit while composing a brand-new
     * announcement — inserted images are staged as local previews instead (see
     * getPendingImages) and only actually uploaded once the announcement is saved and a real id
     * exists. */
    announcementId?: string
    /** Hides the image toolbar button entirely — for editors with no image-upload endpoint wired
     * up at all (e.g. resolutions), where staging a blob: preview would just leave a permanently
     * broken image once saved, since nothing would ever upload and replace it. */
    allowImages?: boolean
  }
>(function RichTextEditor({ value, onChange, id, announcementId, allowImages = true }, ref) {
  const uploadImage = useUploadAnnouncementInlineImage()
  const [imageError, setImageError] = useState<string | null>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const pendingImagesRef = useRef<Map<string, File>>(new Map())

  useImperativeHandle(ref, () => ({
    getPendingImages: () => pendingImagesRef.current,
  }))

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Link.configure({ openOnClick: false, autolink: true }),
      Image,
    ],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        id: id ?? '',
        class:
          '[&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-primary [&_a]:underline [&_img]:max-w-full [&_img]:rounded [&_img]:my-2 min-h-[120px] font-sans text-sm text-ink focus:outline-none',
      },
    },
  })

  useEffect(() => {
    if (!editor) return
    if (value !== editor.getHTML()) editor.commands.setContent(value, { emitUpdate: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor])

  if (!editor) return null

  function setLink() {
    if (!editor) return
    const previousUrl = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Link URL', previousUrl ?? 'https://')
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  function handleImageButtonClick() {
    setImageError(null)
    imageInputRef.current?.click()
  }

  /** setImage() leaves the inserted image node selected — inserting again right after (without
   * moving the cursor past it first) would replace it instead of adding a second image. */
  function insertImageAndAdvanceCursor(src: string, alt: string) {
    if (!editor) return
    editor.chain().focus().setImage({ src, alt }).run()
    editor.commands.setTextSelection(editor.state.selection.to)
  }

  async function handleImageSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !editor) return
    setImageError(null)

    if (!announcementId) {
      const localUrl = URL.createObjectURL(file)
      pendingImagesRef.current.set(localUrl, file)
      insertImageAndAdvanceCursor(localUrl, file.name)
      return
    }

    try {
      const result = await uploadImage.mutateAsync({ announcementId, file })
      insertImageAndAdvanceCursor(publicDocumentUrl(result.data.id), file.name)
    } catch (err) {
      setImageError(getApiErrorMessage(err, 'Failed to upload image'))
    }
  }

  return (
    <div className="rounded border border-input bg-card focus-within:border-primary focus-within:ring-[3px] focus-within:ring-green/20">
      <div className="flex items-center gap-0.5 border-b border-input px-2 py-1.5">
        <button
          type="button"
          aria-pressed={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={TOOLBAR_BUTTON_CLS}
          aria-label="Bold"
        >
          <Bold size={15} />
        </button>
        <button
          type="button"
          aria-pressed={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={TOOLBAR_BUTTON_CLS}
          aria-label="Italic"
        >
          <Italic size={15} />
        </button>
        <button
          type="button"
          aria-pressed={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={TOOLBAR_BUTTON_CLS}
          aria-label="Bulleted list"
        >
          <List size={15} />
        </button>
        <button
          type="button"
          aria-pressed={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={TOOLBAR_BUTTON_CLS}
          aria-label="Numbered list"
        >
          <ListOrdered size={15} />
        </button>
        <button
          type="button"
          aria-pressed={editor.isActive('link')}
          onClick={setLink}
          className={TOOLBAR_BUTTON_CLS}
          aria-label="Link"
        >
          <LinkIcon size={15} />
        </button>
        {allowImages && (
          <>
            <button
              type="button"
              onClick={handleImageButtonClick}
              disabled={uploadImage.isPending}
              className={TOOLBAR_BUTTON_CLS}
              aria-label="Insert image"
              title="Insert image"
            >
              <ImageIcon size={15} />
            </button>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageSelected}
            />
          </>
        )}
      </div>
      <EditorContent editor={editor} className={cn('px-3.5 py-2')} />
      {imageError ? (
        <p className="border-t border-input px-3.5 py-1.5 text-xs text-red-600">{imageError}</p>
      ) : null}
    </div>
  )
})
